import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Square, Home, Tag, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useProjects, resolveDescription } from '../hooks/useProjects';
import { useTranslation } from 'react-i18next';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(0);
  const { properties, loading } = useProjects();

  const property = properties.find(p => String(p.id) === String(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (property) {
      document.title = `${property.title} | Fundora Global`;
    } else {
      document.title = 'Property Not Found | Fundora Global';
    }
  }, [property]);

  if (!property) {
    if (loading) {
      return <div className="min-h-screen pt-20 bg-[#F7FDFF]" data-testid="property-loading" />;
    }
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F7FDFF]">
        <div className="text-center" data-testid="property-not-found">
          <h1 className="text-3xl font-bold text-[#102131] mb-4">{t('propertyDetail.notFound')}</h1>
          <Link to="/properties">
            <Button className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90">
              <ArrowLeft className="mr-2" size={18} />
              {t('propertyDetail.backToCases')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const translateText = (text) => {
    if (!text) return text;
    return text
      .replace(/^From /i, `${t('property.from')} `)
      .replace(/^from /i, `${t('property.from')} `)
      .replace(/\(terrace\)/i, `(${t('property.terrace')})`)
      .replace(/\(balcony\)/i, `(${t('property.balcony')})`);
  };

  const getDescription = () => resolveDescription(property, t, i18n.language);

  const getLocation = () => {
    const locationMap = {
      'Northern Cyprus': t('regions.northernCyprus.name'),
      'Bali (Indonesia)': t('regions.bali.name'),
      'Vietnam': t('regions.vietnam.name'),
      'Maldives': t('regions.maldives.name')
    };
    return locationMap[property.location] || property.location;
  };

  const getPropertyType = () => {
    const typeMap = {
      'Villa': t('property.villa'),
      'Apartment': t('property.apartment'),
      'Mixed': t('property.mixed'),
      'Studio': t('property.studio')
    };
    return typeMap[property.type] || property.type;
  };

  const images = property.images && property.images.length > 0 ? property.images : [property.image];

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen pt-20 bg-[#F7FDFF]" data-testid="property-detail-page">
      {/* Back Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-[#102131]/70 hover:text-[#CDA259] transition-colors text-sm font-medium"
            data-testid="back-button"
          >
            <ArrowLeft size={16} className="mr-2" />
            {t('propertyDetail.backToCases')}
          </button>
        </div>
      </div>

      {/* Image Gallery Section */}
      <section className="bg-[#102131]">
        <div className="max-w-7xl mx-auto">
          {/* Main Image */}
          <div className="relative w-full h-[320px] sm:h-[420px] md:h-[520px] overflow-hidden">
            <img
              src={images[selectedImage]}
              alt={`${property.title} - ${selectedImage + 1}`}
              className="w-full h-full object-cover"
              data-testid="main-property-image"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#102131]/60 via-transparent to-transparent" />

            {/* Image navigation arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors"
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft size={20} className="text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-colors"
                  data-testid="next-image-btn"
                >
                  <ChevronRight size={20} className="text-white" />
                </button>
              </>
            )}

            {/* Image counter */}
            {images.length > 1 && (
              <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full">
                {selectedImage + 1} / {images.length}
              </div>
            )}

            {/* Title overlay on image */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90">
                    {getPropertyType()}
                  </Badge>
                  {property.featured && (
                    <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30">
                      {t('property.featured')}
                    </Badge>
                  )}
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2" data-testid="property-title">
                  {property.title}
                </h1>
                <div className="flex items-center text-white/80">
                  <MapPin size={18} className="mr-2 text-[#CDA259]" />
                  <span className="text-base md:text-lg">{getLocation()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="px-4 py-3 bg-[#0d1a26] flex gap-2 overflow-x-auto" data-testid="thumbnail-strip">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`flex-shrink-0 w-20 h-14 md:w-24 md:h-16 rounded-md overflow-hidden border-2 transition-all ${
                    idx === selectedImage
                      ? 'border-[#CDA259] opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-90'
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Property Details */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Price Card */}
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-[#CDA259]/10">
                <p className="text-sm text-gray-500 uppercase tracking-wider mb-1 font-medium">{t('propertyDetail.price')}</p>
                <p className="text-3xl md:text-4xl font-bold text-[#CDA259]" data-testid="property-price">
                  {translateText(property.price)}
                </p>
              </div>

              {/* Key Details Grid */}
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-[#CDA259]/10">
                <h2 className="text-xl font-bold text-[#102131] mb-6">{t('propertyDetail.keyDetails')}</h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#CDA259]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-[#CDA259]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">{t('propertyDetail.location')}</p>
                      <p className="text-[#102131] font-semibold">{getLocation()}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#CDA259]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Home size={20} className="text-[#CDA259]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-medium">{t('propertyDetail.propertyType')}</p>
                      <p className="text-[#102131] font-semibold">{getPropertyType()}</p>
                    </div>
                  </div>

                  {property.address && (
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#CDA259]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Tag size={20} className="text-[#CDA259]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">{t('propertyDetail.address')}</p>
                        <p className="text-[#102131] font-semibold">{property.address}</p>
                      </div>
                    </div>
                  )}

                  {property.apartmentArea && (
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#CDA259]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Square size={20} className="text-[#CDA259]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">{t('property.apartments')}</p>
                        <p className="text-[#102131] font-semibold">{translateText(property.apartmentArea)}</p>
                      </div>
                    </div>
                  )}

                  {property.villaArea && (
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-[#CDA259]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Square size={20} className="text-[#CDA259]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-medium">{t('property.villas')}</p>
                        <p className="text-[#102131] font-semibold">{translateText(property.villaArea)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-[#CDA259]/10">
                <h2 className="text-xl font-bold text-[#102131] mb-4">{t('propertyDetail.description')}</h2>
                <p className="text-gray-600 leading-relaxed text-base md:text-lg" data-testid="property-description">
                  {getDescription()}
                </p>
              </div>
            </div>

            {/* Right Column - CTA Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <div className="bg-gradient-to-br from-[#102131] to-[#1a3449] rounded-xl shadow-xl p-6 md:p-8 text-white">
                  <h3 className="text-xl font-bold mb-3">{t('propertyDetail.interested')}</h3>
                  <p className="text-white/70 mb-6 text-sm leading-relaxed">
                    {t('propertyDetail.getAdvice')}
                  </p>
                  <Link to="/contact" data-testid="book-consultation-btn">
                    <Button className="w-full bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold py-6 text-base transition-all duration-200 hover:scale-[1.02]">
                      {t('nav.bookConsultation')}
                      <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </Link>

                  <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                    <div className="flex items-center text-sm text-white/60">
                      <div className="w-2 h-2 bg-[#CDA259] rounded-full mr-3" />
                      {t('propertyDetail.directPayments')}
                    </div>
                    <div className="flex items-center text-sm text-white/60">
                      <div className="w-2 h-2 bg-[#CDA259] rounded-full mr-3" />
                      {t('propertyDetail.legalSupport')}
                    </div>
                    <div className="flex items-center text-sm text-white/60">
                      <div className="w-2 h-2 bg-[#CDA259] rounded-full mr-3" />
                      {t('propertyDetail.remoteAvailable')}
                    </div>
                  </div>
                </div>

                {/* Browse More */}
                <div className="mt-6 bg-white rounded-xl shadow-md p-6 border border-[#CDA259]/10">
                  <Link
                    to="/properties"
                    className="inline-flex items-center text-[#102131] hover:text-[#CDA259] transition-colors font-medium"
                    data-testid="browse-more-link"
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    {t('propertyDetail.browseMore')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyDetail;
